import { useSessionUser } from '@/hooks/use-session'
import { companyDirectoryOwnerId, effectiveCompanyRole } from 'shared/pure'
import type { CompanyPermission, EmployeeRole } from 'shared'

type SessionUser = ReturnType<typeof useSessionUser>

/** `usePermissions().isCompanyView` for a caller with no hooks: a loader choosing which audience's list to warm. */
export function isCompanyViewer(user: SessionUser | undefined): boolean {
  const isEmployee = user?.type === 'applicant' && user.isEmployee
  return user?.type === 'company' || (isEmployee && user?.interfaceMode === 'company')
}

// UX only: `can` tests the permission set the SERVER resolved, so the client never recomputes a role table
// and companyProcedure still rejects anything a hidden-but-reachable action tries.
export function usePermissions(): {
  role: EmployeeRole | null
  isCompanyContext: boolean
  can: (permission: CompanyPermission) => boolean
  // Block C: render the COMPANY interface (a company account, or a promoted employee viewing 'company').
  isCompanyView: boolean
  // The current promoted-employee view (server-resolved + role-gated); 'employee' for everyone else.
  interfaceMode: 'employee' | 'company'
  // A promoted admin/authorized employee who can toggle between their employee profile and the company view.
  canToggleInterface: boolean
  // Directory reach, not which interface is on screen: an admin compares against the whole directory from
  // "Mi perfil" too. Equipos stay per-team restricted, which the server enforces.
  canBrowseCompanyDirectory: boolean
} {
  const user = useSessionUser()

  const role: EmployeeRole | null = user ? effectiveCompanyRole(user) : null

  const isEmployee = user?.type === 'applicant' && user.isEmployee
  const interfaceMode = user?.interfaceMode ?? 'employee'
  const canToggleInterface =
    isEmployee && user?.activeCompanyId != null && (role === 'administrator' || role === 'authorized')

  return {
    role,
    // Operating inside a company context: a company account, or an employee acting under an employer.
    isCompanyContext: role != null,
    // The same shared function the server gates on, so the dropdown cannot offer what the selectors refuse.
    canBrowseCompanyDirectory: user != null && companyDirectoryOwnerId(user) != null,
    can: (permission: CompanyPermission) => user?.permissions?.includes(permission) ?? false,
    isCompanyView: isCompanyViewer(user),
    interfaceMode,
    canToggleInterface,
  }
}
